import { useEffect, useRef } from 'react';

// Smooth progress ring: HTML5 video onTimeUpdate fires at ~4-15Hz with
// irregular gaps, which makes the gradient stroke around each round
// video/audio jerk forward in noticeable steps. Drive currentTime from
// a single requestAnimationFrame loop while anything is playing so the
// SVG dashoffset updates at the browser's refresh rate (~60fps).
export function useRoundProgressTicker({
  insertedRoundVideos, setInsertedRoundVideos,
  insertedRoundAudios, setInsertedRoundAudios,
}) {
  const progressRafRef = useRef(null);
  const anyVideoPlaying = insertedRoundVideos.some(v => v.playing);
  const anyAudioPlaying = insertedRoundAudios.some(a => a.playing);
  useEffect(() => {
    if (!anyVideoPlaying && !anyAudioPlaying) {
      if (progressRafRef.current) {
        cancelAnimationFrame(progressRafRef.current);
        progressRafRef.current = null;
      }
      return;
    }
    const tick = () => {
      if (anyVideoPlaying) {
        setInsertedRoundVideos(prev => {
          let changed = false;
          const next = prev.map(item => {
            if (!item.playing) return item;
            const el = document.querySelector(`video[data-round-id="${item.id}"]`);
            if (!el) return item;
            if (Math.abs((el.currentTime || 0) - (item.currentTime || 0)) < 0.001) return item;
            changed = true;
            return { ...item, currentTime: el.currentTime, duration: el.duration || item.duration || 1 };
          });
          return changed ? next : prev;
        });
      }
      if (anyAudioPlaying) {
        setInsertedRoundAudios(prev => {
          let changed = false;
          const next = prev.map(item => {
            if (!item.playing) return item;
            const el = document.querySelector(`audio[data-round-id="${item.id}"]`);
            if (!el) return item;
            if (Math.abs((el.currentTime || 0) - (item.currentTime || 0)) < 0.001) return item;
            changed = true;
            return { ...item, currentTime: el.currentTime, duration: el.duration || item.duration || 1 };
          });
          return changed ? next : prev;
        });
      }
      progressRafRef.current = requestAnimationFrame(tick);
    };
    progressRafRef.current = requestAnimationFrame(tick);
    return () => {
      if (progressRafRef.current) {
        cancelAnimationFrame(progressRafRef.current);
        progressRafRef.current = null;
      }
    };
  }, [anyVideoPlaying, anyAudioPlaying, setInsertedRoundVideos, setInsertedRoundAudios]);
}
